import Velocity from 'velocity-animate';

import Component from '../component/component';
import Helpers, { debounce, DebouncedFunction } from '../helpers/helpers';

import './image-scroll.scss';

interface IImageScrollSettings {
    animationDuration: number;
    arrowsSelector: string;
    arrowLeftSelector: string;
    arrowRightSelector: string;
    containerSelector: string;
    easing: string | number[];
    hoverClass: string;
    imageSelector: string;
    scrollAmount: number;
    visibleClass: string;
}

export type TImageScrollDirection = 'right' | 'left';

export default class ImageScroll extends Component {
    static initSelector: string = '.image-scroll';

    arrowLeft: JQuery;
    arrowRight: JQuery;
    arrowRightDown: boolean;
    arrows: JQuery;
    container: JQuery;
    debouncedResizeHandler: DebouncedFunction<() => void>;
    htmlImage: HTMLImageElement;
    image: JQuery;
    isActive: boolean;
    isAnimating: boolean;
    isHover: boolean;
    leftOffset: number;
    mouseDownTimeout: ReturnType<typeof setTimeout>;
    scrollAmount: number;
    settings: IImageScrollSettings;

    constructor(target: HTMLElement) {
        super(target);

        this.settings = {
            animationDuration: 300,
            arrowLeftSelector: '.image-scroll__arrow--left',
            arrowRightSelector: '.image-scroll__arrow--right',
            arrowsSelector: '.image-scroll__arrows',
            containerSelector: '.image-scroll__image-container',
            easing: 'linear',
            hoverClass: 'image-scroll--hover',
            imageSelector: '.image-scroll__img',
            scrollAmount: 150,
            visibleClass: 'is-visible',
        };

        this.arrowLeft = this.element.find(this.settings.arrowLeftSelector);
        this.arrowRight = this.element.find(this.settings.arrowRightSelector);
        this.arrows = this.element.find(this.settings.arrowsSelector);
        this.container = this.element.find(this.settings.containerSelector);
        this.htmlImage = new Image();
        this.image = this.element.find(this.settings.imageSelector);
        this.isAnimating = false;
        this.isHover = this.element.hasClass(this.settings.hoverClass);
        this.leftOffset = 0;
        this.scrollAmount = this.settings.scrollAmount;

        this.init();
    }

    init(): void {
        this.htmlImage.src = this.image.attr('src');

        this.htmlImage.onload = (): void => {
            this.onResize();
            this.debouncedResizeHandler = debounce(this.onResize.bind(this), 50);
            $(window).on('resize', this.debouncedResizeHandler);
        };

        let downEvents: string = 'touchstart';
        let upEvents: string = 'touchend';

        downEvents += this.isHover && !Helpers.isMobileDevice ? ' mouseenter' : ' mousedown';
        upEvents += this.isHover && !Helpers.isMobileDevice ? ' mouseleave': ' mouseup';

        this.arrowRight.on(downEvents, this.onMouseDown.bind(this, 'right'));
        this.arrowRight.on(upEvents, this.onMouseUp.bind(this, 'right'));

        this.arrowLeft.on(downEvents, this.onMouseDown.bind(this, 'left'));
        this.arrowLeft.on(upEvents, this.onMouseUp.bind(this, 'left'));
    }

    onMouseDown(direction: TImageScrollDirection): void {
        this.isActive = false;

        // Start scrolling
        this.mouseDownTimeout = setInterval((): void => {
            this.isActive = true;

            if (direction === 'right' && !this.isAnimating) {
                this.scrollRight(100);
            } else if (direction === 'left' && !this.isAnimating) {
                this.scrollLeft(100);
            }
        }, 10);
    }

    onMouseUp(direction: TImageScrollDirection): void {
        // Only if clicked (not mousedown):
        if (!this.isActive && !this.isAnimating) {
            if (direction === 'right') {
                this.scrollRight();
            } else if (direction === 'left') {
                this.scrollLeft();
            }
        }

        // End scrolling
        clearInterval(this.mouseDownTimeout);
    }

    onResize(): void {
        if (this.container.width() < this.image.width()) {
            this.showArrows();
        } else {
            this.hideArrows();
        }

        if (this.leftOffset + this.scrollAmount > 0 || this.container.width() > this.image.width()) {
            this.leftOffset = 0;

            this.animate();
        } else if (this.container.width() - (this.leftOffset - this.scrollAmount) >= this.image.width()) {
            this.leftOffset -= (this.image.width() - (this.container.width() - this.leftOffset));

            this.animate();
        }
    }

    scrollRight(amount: number = this.scrollAmount): void {
        if (this.container.width() - (this.leftOffset - amount) < this.image.width()) {
            this.leftOffset -= amount;
        } else {
            this.leftOffset -= (this.image.width() - (this.container.width() - this.leftOffset));
        }

        this.animate();
    }

    scrollLeft(amount: number = this.scrollAmount): void {
        if (this.leftOffset + amount > 0) {
            this.leftOffset = 0;
        } else {
            this.leftOffset += amount;
        }

        this.animate();
    }

    animate(): void {
        if (!this.isAnimating) {
            Velocity(this.image, {
                left: this.leftOffset,
            }, {
                begin: this.onAnimationBegin.bind(this),
                complete: this.onAnimateComplete.bind(this),
                duration: this.settings.animationDuration,
                easing: this.settings.easing,
            });
        }
    }

    onAnimationBegin(): void {
        this.isAnimating = true;
    }

    onAnimateComplete(): void {
        this.isAnimating = false;
    }

    showArrows(): void {
        this.arrows.addClass(this.settings.visibleClass);
    }

    hideArrows(): void {
        this.arrows.removeClass(this.settings.visibleClass);
    }
}
